import { apiClient } from './client';

export interface MetricsSlice {
  total_sessions: number;
  active_merchants: number;
  active_users: number;
  sessions_per_merchant: number | null;
  positive_ratings: number;
  negative_ratings: number;
  no_rating_sessions: number;
  support_escalations: number;
  support_form_submissions: number;
  resolved_sessions: number;
  drop_off_sessions: number;
  resolution_rate: number | null;
  drop_off_rate: number | null;
}

export interface MetricsPayload {
  current: MetricsSlice;
  previous: MetricsSlice;
  theme_counts?: Record<string, number>;
}

export interface IntentBucket {
  intent: string;
  label: string;
  session_count: number;
}

export interface IntentsPayload {
  buckets: IntentBucket[];
  unclassified_count: number;
  classified_rate: number | null;
}

export interface ToolCallBucket {
  tool: string;
  label: string;
  call_count: number;
  session_count: number;
}

export interface ToolCallsPayload {
  buckets: ToolCallBucket[];
}

export interface QuestionTheme {
  key: string;
  label: string;
}

export interface ThemesPayload {
  themes: QuestionTheme[];
}

export interface SessionShop {
  id: number;
  name: string;
  domain: string | null;
}

export interface SessionUser {
  id: number;
  name: string | null;
  email: string | null;
}

export interface SessionListItem {
  session_id: string;
  conversation_id: string | null;
  context: string | null;
  rating: number | null;
  feedback: string | null;
  summary: string | null;
  session_intent: string | null;
  summarized_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  has_contact_form: boolean;
  via_slack?: boolean;
  message_feedback_count?: number;
  message_count?: number;
  tools?: string[];
  shop: SessionShop | null;
  user: SessionUser | null;
}

export interface SessionDetailPayload {
  session: Omit<SessionListItem, 'shop' | 'user'> & {
    shop: SessionShop | null;
    user: SessionUser | null;
    has_contact_form: boolean;
    message_feedback_count?: number;
  };
  messages: Array<{
    message_id: string;
    role: string;
    content: string;
    rating: number | null;
    feedback: string | null;
    tool_calls: Record<string, unknown> | unknown[] | null;
    created_at: string | null;
  }>;
}

export interface LaravelPaginatorEnvelope<T> {
  data: T[];
  meta: {
    total: number;
    current_page?: number;
    per_page?: number;
    last_page?: number;
  };
}

export type SessionRating = 'positive' | 'negative' | 'none';

export type SessionSource = 'slack' | 'admin';

export type SessionsSortColumn = 'created_at' | 'rating' | 'shop_name' | 'user_email';

export type SessionsSortDirection = 'asc' | 'desc';

export type SessionIntentFilter =
  | 'instructional'
  | 'troubleshooting'
  | 'configuration'
  | 'other';

export interface SessionsQueryParams {
  from: string;
  to: string;
  page?: number;
  per_page?: number;
  rating?: SessionRating;
  has_feedback?: boolean;
  has_contact_form?: boolean;
  theme?: string;
  intent?: SessionIntentFilter;
  tool?: string;
  shop_id?: number;
  source?: SessionSource;
  sort?: SessionsSortColumn;
  direction?: SessionsSortDirection;
  drop_off?: boolean;
}

function buildSessionsQueryRecord (params: SessionsQueryParams): Record<string, string | number | undefined> {
  const query: Record<string, string | number | undefined> = {
    from: params.from,
    to: params.to,
    page: params.page,
    per_page: params.per_page,
    rating: params.rating,
    theme: params.theme,
    intent: params.intent,
    tool: params.tool,
    shop_id: params.shop_id,
    source: params.source,
    sort: params.sort,
    direction: params.direction,
  };

  if (params.has_feedback === true) {
    query.has_feedback = 'true';
  } else if (params.has_feedback === false) {
    query.has_feedback = 'false';
  }

  if (params.has_contact_form === true) {
    query.has_contact_form = 'true';
  } else if (params.has_contact_form === false) {
    query.has_contact_form = 'false';
  }

  if (params.drop_off === true) {
    query.drop_off = 'true';
  } else if (params.drop_off === false) {
    query.drop_off = 'false';
  }

  return query;
}

export interface MetricsQueryParams {
  from: string;
  to: string;
  theme?: string;
}

export function getMetrics (params: MetricsQueryParams) {
  return apiClient.get<MetricsPayload>('/loop-assistant/metrics', { params });
}

export function getIntents (params: { from: string; to: string }) {
  return apiClient.get<IntentsPayload>('/loop-assistant/intents', { params });
}

export function getToolCalls (params: { from: string; to: string }) {
  return apiClient.get<ToolCallsPayload>('/loop-assistant/tool-calls', { params });
}

export function getThemes () {
  return apiClient.get<ThemesPayload>('/loop-assistant/themes');
}

export function getSessions (params: SessionsQueryParams) {
  return apiClient.get<LaravelPaginatorEnvelope<SessionListItem>>('/loop-assistant/sessions', {
    params: buildSessionsQueryRecord(params),
  });
}

export function exportSessions (params: Omit<SessionsQueryParams, 'page' | 'per_page'>) {
  return apiClient.get<Blob>('/loop-assistant/sessions/export', {
    params: buildSessionsQueryRecord(params),
    responseType: 'blob',
  });
}

export function getSession (sessionId: string) {
  return apiClient.get<SessionDetailPayload>(`/loop-assistant/sessions/${sessionId}`);
}
